import type { UploadChatAttachment } from "@amoreg/contracts";
import { readFileAsDataUrl } from "../components/ChatView.logic";
import {
  fileAttachmentMimeType,
  reviewInlineFileAttachments,
} from "../components/chat/attachmentFiles";

export type FindingReviewResult = { threadId: string } | { error: string };

/** Same-draft retries retain the UUID; the server deduplicates comments and runs durably. */
export function createFindingReviewSubmission(url: string, onSubmitted: () => void) {
  let attempt: { text: string; requestId: string; files: readonly File[] } | null = null;
  let pending: Promise<FindingReviewResult> | null = null;
  return (raw: string, files: readonly File[] = []): Promise<FindingReviewResult> => {
    const text = raw.trim();
    if (!text || text.length > 3900)
      return Promise.resolve({ error: "Write a request of up to 3,900 characters." });
    if (pending) return pending;
    const checked = reviewInlineFileAttachments({ files, reservedCount: 0 });
    if (checked.error) return Promise.resolve({ error: checked.error });
    if (
      attempt?.text !== text ||
      attempt.files.length !== files.length ||
      files.some((file, i) => file !== attempt!.files[i])
    ) {
      attempt = { text, requestId: crypto.randomUUID(), files: [...files] };
    }
    const requestId = attempt.requestId;
    pending = (async () => {
      try {
        const attachments: UploadChatAttachment[] = [];
        for (const file of files) {
          attachments.push({
            type: "file",
            name: file.name,
            mimeType: fileAttachmentMimeType(file),
            sizeBytes: file.size,
            dataUrl: await readFileAsDataUrl(file),
          });
        }
        const body = JSON.stringify({
          text,
          requestId,
          ...(attachments.length ? { attachments } : {}),
        });
        const response = await fetch(url, {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body,
        });
        const value: unknown = await response.json().catch(() => null);
        if (
          response.ok &&
          value &&
          typeof value === "object" &&
          "threadId" in value &&
          typeof value.threadId === "string"
        ) {
          attempt = null;
          return { threadId: value.threadId };
        }
        return {
          error:
            value &&
            typeof value === "object" &&
            "error" in value &&
            typeof value.error === "string"
              ? value.error
              : "Inspector could not be started. Your request has been kept; try again.",
        };
      } catch {
        return { error: "Inspector could not be reached. Your request has been kept; try again." };
      } finally {
        pending = null;
        onSubmitted();
      }
    })();
    return pending;
  };
}
